import React from 'react'
import { FormControl, MenuItem, Select, Typography } from '@material-ui/core'
import { useLabeledSelectStyles } from './labeled-select.styles'

export type LabeledSelectOption<T extends string | number = string | number> = {
  value: T
  label: string
}

export type LabeledSelectProps<T extends string | number = string | number> = {
  label: string
  value: T
  options: LabeledSelectOption<T>[]
  onChange: (value: T) => void
  disabled?: boolean
  idSuffix?: string
  className?: string
  /** Same width behavior as Show items: full width on small screens, auto from md */
  fluid?: boolean
  /** When `fluid` is false, sets min-width on the wrapper (px). Ignored when `fluid`. */
  minWidth?: number
  selectClassName?: string
}

export function LabeledSelect<T extends string | number>(
  props: LabeledSelectProps<T>
): React.ReactElement {
  const {
    label,
    value,
    options,
    onChange,
    disabled = false,
    idSuffix = '',
    className,
    fluid = false,
    minWidth,
    selectClassName,
  } = props

  const classes = useLabeledSelectStyles()
  const baseId = 'labeled-select' + (idSuffix ? `-${idSuffix}` : '')
  const captionId = `${baseId}-caption`

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const raw = event.target.value
    const match = options.find((o) => String(o.value) === String(raw))
    if (match) {
      onChange(match.value)
    }
  }

  return (
    <div
      className={[classes.wrapperRoot, fluid ? classes.wrapperFluid : '', className]
        .filter(Boolean)
        .join(' ')}
      style={!fluid && minWidth != null ? { minWidth } : undefined}
    >
      <Typography
        id={captionId}
        component="label"
        htmlFor={baseId}
        className={classes.fieldLabel}
      >
        {label}
      </Typography>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.formControl}
        disabled={disabled}
      >
        <Select
          id={baseId}
          disabled={disabled}
          variant="outlined"
          value={value}
          onChange={handleChange}
          displayEmpty
          className={selectClassName}
          MenuProps={{
            PaperProps: {
              className: classes.menuPaper,
            },
          }}
          inputProps={{ 'aria-labelledby': captionId }}
        >
          {options.map((opt) => (
            <MenuItem
              key={String(opt.value)}
              value={opt.value as string | number}
              className={classes.menuItem}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}
